import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { Package, Users, ReceiptText, TrendingUp, Plus, Trash2, Save, X, KeyRound } from "lucide-react";

const CATEGORY_OPTIONS = [
  { slug: "starter-packs", name: "Starter Packs" },
  { slug: "weapon-packs", name: "Weapon Packs" },
  { slug: "gang-packs", name: "Gang Packs" },
  { slug: "one-of-one", name: "1 of 1 Cars" },
  { slug: "starter-free", name: "Starter Free Car" },
  { slug: "car-drop", name: "Car Drop Cars" },
  { slug: "mlos", name: "MLO's" },
  { slug: "businesses", name: "Businesses" },
];

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-white/10 bg-[color:var(--bg-surface)] p-6">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <Icon className="w-4 h-4 text-zinc-500" />
      </div>
      <div className="font-display text-4xl text-white mt-3">{value}</div>
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      slug: "",
      name: "",
      category: "starter-packs",
      tagline: "",
      description: "",
      price: 0,
      image: "",
      features: [],
      is_free: false,
      is_featured: false,
      is_active: true,
      stock: null,
      sort_order: 0,
    }
  );
  const [featuresText, setFeaturesText] = useState((initial?.features || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await onSave({
        ...form,
        price: parseFloat(form.price) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        stock: form.stock === "" || form.stock == null ? null : parseInt(form.stock),
        features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onCancel} />
      <div className="relative w-full max-w-3xl bg-[color:var(--bg-surface)] border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="uj-bar" />
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="eyebrow mb-1">/ {initial ? "EDIT" : "CREATE"} PRODUCT</div>
              <h2 className="font-display text-3xl text-white">{initial ? initial.name : "New Item"}</h2>
            </div>
            <button onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-1">NAME</label>
              <input data-testid="pform-name" className="terminal-input" value={form.name} onChange={(e) => update("name", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-1">SLUG</label>
              <input data-testid="pform-slug" className="terminal-input" value={form.slug} onChange={(e) => update("slug", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-1">CATEGORY</label>
              <select data-testid="pform-category" className="terminal-input" value={form.category} onChange={(e) => update("category", e.target.value)}>
                {CATEGORY_OPTIONS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-1">PRICE (GBP)</label>
              <input data-testid="pform-price" type="number" step="0.01" className="terminal-input" value={form.price} onChange={(e) => update("price", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-1">TAGLINE</label>
              <input data-testid="pform-tagline" className="terminal-input" value={form.tagline || ""} onChange={(e) => update("tagline", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-1">IMAGE URL</label>
              <input data-testid="pform-image" className="terminal-input" value={form.image} onChange={(e) => update("image", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-1">DESCRIPTION</label>
              <textarea data-testid="pform-description" rows={4} className="terminal-input" value={form.description} onChange={(e) => update("description", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow block mb-1">FEATURES (ONE PER LINE)</label>
              <textarea data-testid="pform-features" rows={4} className="terminal-input" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-1">STOCK (BLANK = UNLIMITED)</label>
              <input data-testid="pform-stock" className="terminal-input" value={form.stock ?? ""} onChange={(e) => update("stock", e.target.value)} />
            </div>
            <div>
              <label className="eyebrow block mb-1">SORT ORDER</label>
              <input data-testid="pform-sort" type="number" className="terminal-input" value={form.sort_order} onChange={(e) => update("sort_order", e.target.value)} />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => update("is_featured", e.target.checked)} /> Featured
              </label>
              <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">
                <input type="checkbox" checked={form.is_free} onChange={(e) => update("is_free", e.target.checked)} /> Free
              </label>
              <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300">
                <input type="checkbox" checked={form.is_active} onChange={(e) => update("is_active", e.target.checked)} /> Active
              </label>
            </div>
          </div>

          {error && <div className="mt-4 font-mono text-[11px] text-[color:var(--accent-red)]">{error}</div>}

          <div className="mt-8 flex items-center gap-3">
            <button onClick={save} disabled={busy} data-testid="pform-save" className="btn-brutal h-11 px-6 border border-white/15 text-white font-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> {busy ? "Saving..." : "Save"}
            </button>
            <button onClick={onCancel} className="h-11 px-6 border border-white/10 text-zinc-300 font-mono text-[11px] tracking-[0.24em] uppercase hover:text-white">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [s, ps, os] = await Promise.all([api.adminStats(), api.products({}), api.adminOrders()]);
    setStats(s);
    setProducts(ps);
    setOrders(os);
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const statsView = useMemo(
    () => stats || { total_orders: 0, paid_orders: 0, total_users: 0, revenue: 0, total_products: 0 },
    [stats]
  );

  if (ready && (!user || !user.is_admin)) return <Navigate to="/admin/login" replace />;

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.adminDeleteProduct(id);
    load();
  };

  const updateOrderStatus = async (id, status) => {
    await api.adminUpdateOrder(id, status);
    load();
  };

  return (
    <main data-testid="admin-page" className="pt-24 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 py-12">
      <div className="flex items-start justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">/ COMMAND CENTRE</div>
          <h1 className="font-display text-white text-5xl md:text-7xl tracking-tighter leading-[0.9]">
            OPS.<span className="text-[color:var(--accent-red)]">DASHBOARD</span>
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <StatCard label="REVENUE" value={`£${Number(statsView.revenue).toFixed(2)}`} icon={TrendingUp} />
        <StatCard label="ORDERS" value={statsView.total_orders} icon={ReceiptText} />
        <StatCard label="PAID" value={statsView.paid_orders} icon={ReceiptText} />
        <StatCard label="USERS" value={statsView.total_users} icon={Users} />
      </div>

      <div className="flex items-center gap-1 border border-white/10 w-fit mb-6">
        {["products", "orders", "settings"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`h-10 px-6 font-mono text-[11px] tracking-[0.24em] uppercase ${tab === t ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-white uppercase">
              <Package className="w-4 h-4 inline mr-2 -mt-1" /> Catalogue
            </h2>
            <button
              onClick={() => setCreating(true)}
              data-testid="admin-new-product"
              className="btn-brutal h-10 px-4 border border-white/15 text-white font-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" /> New product
            </button>
          </div>
          <div className="border border-white/10">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 font-mono text-[10px] tracking-[0.24em] uppercase text-zinc-500">
              <div className="col-span-4">NAME</div>
              <div className="col-span-2">CATEGORY</div>
              <div className="col-span-2">PRICE</div>
              <div className="col-span-2">STATE</div>
              <div className="col-span-2 text-right">ACTIONS</div>
            </div>
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.slug}`} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center font-mono text-[12px]">
                <div className="col-span-4 text-white truncate">{p.name}</div>
                <div className="col-span-2 text-zinc-400">{p.category}</div>
                <div className="col-span-2 text-zinc-400">{p.is_free ? "FREE" : `£${Number(p.price).toFixed(2)}`}</div>
                <div className="col-span-2 text-zinc-400">
                  {p.is_active ? "ACTIVE" : "HIDDEN"}{p.is_featured ? " · ★" : ""}
                </div>
                <div className="col-span-2 flex items-center gap-2 justify-end">
                  <button onClick={() => setEditing(p)} className="h-8 px-3 border border-white/15 text-zinc-300 hover:text-white text-[10px] uppercase tracking-[0.2em]">Edit</button>
                  <button onClick={() => del(p.id)} className="h-8 w-8 border border-white/15 text-zinc-400 hover:text-[color:var(--accent-red)] flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "orders" && (
        <section>
          <h2 className="font-display text-2xl text-white uppercase mb-4">
            <ReceiptText className="w-4 h-4 inline mr-2 -mt-1" /> Orders
          </h2>
          <div className="border border-white/10 overflow-x-auto">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 font-mono text-[10px] tracking-[0.24em] uppercase text-zinc-500 min-w-[900px]">
              <div className="col-span-2">ID</div>
              <div className="col-span-3">PRODUCT</div>
              <div className="col-span-2">BUYER</div>
              <div className="col-span-2">FIVEM</div>
              <div className="col-span-1">£</div>
              <div className="col-span-2 text-right">STATUS</div>
            </div>
            {orders.map((o) => (
              <div key={o.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center font-mono text-[12px] min-w-[900px]">
                <div className="col-span-2 text-zinc-500">#{o.id.slice(0, 8).toUpperCase()}</div>
                <div className="col-span-3 text-white truncate">{o.product_name}</div>
                <div className="col-span-2 text-zinc-300 truncate">{o.discord_username || "-"}</div>
                <div className="col-span-2 text-zinc-400 truncate">{o.fivem_id || "-"}</div>
                <div className="col-span-1 text-zinc-300">£{Number(o.amount).toFixed(2)}</div>
                <div className="col-span-2 flex items-center gap-1 justify-end">
                  <select
                    data-testid={`admin-order-status-${o.id}`}
                    value={o.status}
                    onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                    className="h-8 bg-[color:var(--bg-elev)] border border-white/15 text-white text-[11px] uppercase px-2"
                  >
                    <option value="pending">PENDING</option>
                    <option value="paid">PAID</option>
                    <option value="delivered">DELIVERED</option>
                    <option value="cancelled">CANCELLED</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "settings" && <SettingsPanel />}

      {editing && <ProductForm initial={editing} onCancel={() => setEditing(null)} onSave={async (form) => { await api.adminUpdateProduct(editing.id, form); setEditing(null); load(); }} />}
      {creating && <ProductForm onCancel={() => setCreating(false)} onSave={async (form) => { await api.adminCreateProduct(form); setCreating(false); load(); }} />}
    </main>
  );
}

function SettingsPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New password confirmation does not match.");
      return;
    }
    setBusy(true);
    try {
      await api.changeAdminPassword(current, next);
      setMessage("Password updated. Use it next time you log in.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-settings" className="max-w-xl">
      <h2 className="font-display text-2xl text-white uppercase mb-4">
        <KeyRound className="w-4 h-4 inline mr-2 -mt-1" /> Change password
      </h2>
      <form onSubmit={submit} className="border border-white/10 bg-[color:var(--bg-surface)] p-6 space-y-4">
        <div>
          <label className="eyebrow block mb-2">CURRENT PASSWORD</label>
          <input
            data-testid="settings-current-password"
            type="password"
            className="terminal-input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">NEW PASSWORD (MIN 8 CHARS)</label>
          <input
            data-testid="settings-new-password"
            type="password"
            className="terminal-input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">CONFIRM NEW PASSWORD</label>
          <input
            data-testid="settings-confirm-password"
            type="password"
            className="terminal-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        {error && <div className="font-mono text-[11px] text-[color:var(--accent-red)]">{error}</div>}
        {message && <div className="font-mono text-[11px] text-emerald-400">{message}</div>}

        <button
          type="submit"
          disabled={busy || !current || !next || !confirm}
          data-testid="settings-save-password"
          className="btn-brutal h-11 px-6 border border-white/15 text-white font-mono text-[11px] tracking-[0.24em] uppercase disabled:opacity-50"
        >
          {busy ? "Updating..." : "Update password"}
        </button>
      </form>
    </section>
  );
}
